import { t } from 'i18next';
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  ChevronDown,
  X,
} from 'lucide-react';

import { Card } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { useTimeAgo } from '@/lib/utils';
import { AdminHealthOverview } from '@activepieces/shared';

import { SLOW_PROBE_MS, UNIX_SECONDS_TO_MS } from './constants';

const ServiceLine = ({
  label,
  isOk,
  durationMs,
}: {
  label: string;
  isOk: boolean;
  durationMs: number;
}) => (
  // Status is carried by an icon as well as a color: red-green alone is the one
  // pair a deuteranope cannot separate.
  <div className="flex items-center gap-2 text-sm min-w-0">
    {isOk ? (
      <Check className="size-3.5 text-success shrink-0" />
    ) : (
      <X className="size-3.5 text-destructive shrink-0" />
    )}
    <span className={isOk ? 'text-muted-foreground truncate' : 'truncate'}>
      {label}
    </span>
    {/* A dependency answering in seconds shows the same green tick as a healthy
        one; the timing is what makes "up but dying" visible. */}
    <span
      className={
        durationMs >= SLOW_PROBE_MS
          ? 'ml-auto shrink-0 text-xs tabular-nums font-medium text-warning'
          : 'ml-auto shrink-0 text-xs tabular-nums text-muted-foreground'
      }
    >
      {t('{duration} ms', { duration: durationMs })}
    </span>
  </div>
);

// Same shape as the scheduled-jobs panel: one line of verdict, detail behind a
// chevron. Nine green pills only matter once something is actually broken.
export const ServicesPanel = ({ health }: { health: AdminHealthOverview }) => {
  const checkedAgo = useTimeAgo(
    new Date(health.servicesCheckedAt * UNIX_SECONDS_TO_MS),
  );
  const failing = health.services.filter(
    (service) => service.status === 'failed',
  );
  const isHealthy = failing.length === 0;

  return (
    <Card
      className={
        isHealthy
          ? 'flex flex-col overflow-hidden'
          : 'flex flex-col overflow-hidden border-destructive/30'
      }
    >
      <Collapsible>
        <CollapsibleTrigger
          aria-label={t('Show every dependency')}
          className={
            isHealthy
              ? 'group w-full text-left flex items-start gap-2.5 px-4 py-3 hover:bg-muted/40 transition-colors'
              : 'group w-full text-left flex items-start gap-2.5 px-4 py-3 bg-destructive/5 hover:bg-destructive/10 transition-colors'
          }
        >
          {isHealthy ? (
            <CheckCircle2 className="size-4 text-success shrink-0 mt-0.5" />
          ) : (
            <AlertTriangle className="size-4 text-destructive shrink-0 mt-0.5" />
          )}
          <div className="min-w-0 flex-1 flex flex-col gap-0.5">
            <p
              className={
                isHealthy ? 'text-sm' : 'text-sm font-medium text-destructive'
              }
            >
              {isHealthy
                ? t('{dependencies} dependencies, all responding', {
                    dependencies: health.services.length,
                  })
                : t('Not responding: {services}', {
                    services: failing
                      .map((service) => service.label)
                      .join(', '),
                  })}
            </p>
            {/* The healthcheck caches per API process, so two containers can
                answer differently — we date the answer rather than imply one
                global truth. */}
            <p className="text-xs text-muted-foreground">
              {t('Checked {timeAgo} by the instance that served this request', {
                timeAgo: checkedAgo,
              })}
            </p>
          </div>
          <ChevronDown className="size-5 shrink-0 self-center text-muted-foreground group-hover:text-foreground group-data-[state=open]:rotate-180 transition-transform" />
        </CollapsibleTrigger>

        <CollapsibleContent className="overflow-hidden">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-5 gap-y-2 px-4 py-3 border-t">
            {health.services.map((service) => (
              <ServiceLine
                key={service.key}
                label={service.label}
                isOk={service.status === 'ok'}
                durationMs={service.durationMs}
              />
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};
